import { graphic } from "./Graphic";
import { stateManager } from "./StateManager";
import { PanEditionMode } from "./editions/pan/PanEditionMode";
import { DUPLICATE_SELECTION, graphicEvents } from "./GraphicEvents";
import { SelectionEditionMode } from "./editions/selection/SelectionEditionMode";
import { RowEditionMode } from "./editions/rows/RowEditionMode";
import { TextEditionMode } from "./editions/texts/TextEditionMode";
import { PitEditionMode } from "./editions/pit/PitEditionMode";
import { ZoomFingers } from "./ZoomFingers";
import { selection } from "./editions/selection/Selection";

class ContainerEvents {

    constructor() {
        this.bus = document.createElement('div');
        this.mouseCoords = {x: 0, y: 0};
        this.isClicking = false;
        this.isCtrlPressed = false;
        this.overEntities = [];
        this.enable = true;
        this.originalMouseCoords = {x:0, y:0};
    }

    get isPointerOverEntity() {
        return this.overEntities.length > 0;
    }

    init(container) {
        this.zoomFingers = new ZoomFingers();
        this.bindEvents(this.zoomFingers);

        container.interactive = true;
        container.buttonMode = true;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerOut = this.onPointerOut.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);

        container.on('pointerdown', this.onPointerDown);
        container.on('pointermove', this.onPointerMove);
        container.on('pointerout', this.onPointerOut);
        container.on('pointerup', this.onPointerUp);

        document.addEventListener('keydown', (e) => {
            if (!this.enable) return;
            if (e.keyCode === 17) {
                this.isCtrlPressed = true;
                this.onPressCtrl(e);
            }
            if (e.keyCode === 46) this.onPressDelete(e);
            if (e.keyCode === 90) this.onPressZ(e);
            if (e.keyCode === 68) this.onPressD(e);
            if ([37, 38, 39, 40].indexOf(e.keyCode) !== -1) this.onPressArrow(e);
        });
        document.addEventListener('keyup', (e) => {
            if (!this.enable) return;
            if (e.keyCode === 17) {
                this.isCtrlPressed = false;
                this.onStopPressCtrl(e);
            }
            if (e.keyCode === 32) this.onStopPressSpace();
        });
    }

    destroy(container) {
        container.off('pointerdown', this.onPointerDown);
        container.off('pointermove', this.onPointerMove);
        container.off('pointerout', this.onPointerOut);
        container.off('pointerup', this.onPointerUp);

        this.unbindEvents(this.zoomFingers);
    }

    registerCoordinates(e) {
        this.originalMouseCoords = {
            x: Math.round(e.data.global.x),
            y: Math.round(e.data.global.y)
        };
        this.mouseCoords = graphic.withTransformContainer(this.originalMouseCoords);
    }

    onPointerOut(e) {
        if (!this.enable) return;

        if (this.isClicking) {
            this.bus.dispatchEvent(new CustomEvent('onCancelClick', { detail: { originalEvent: e } }));
            this.isClicking = false;
        }
    }

    onPointerDown(e) {
        if (!this.enable) return;

        this.registerCoordinates(e);
        this.isClicking = true;
        this.bus.dispatchEvent(new CustomEvent('onStartClick', { detail: { originalEvent: e } }));
    }

    onPointerMove(e) {
        if (!this.enable) return;

        this.registerCoordinates(e);
        this.bus.dispatchEvent(new CustomEvent('onMoveMouse'));
    }

    onPointerUp(e) {
        if (!this.enable) return;

        this.isClicking = false;
        this.bus.dispatchEvent(new CustomEvent('onFinishClick', { detail: { originalEvent: e } }));
    }

    onPressDelete() {
        this.bus.dispatchEvent(new CustomEvent('onPressDelete'));
    }

    onPressZ() {
        if (this.isCtrlPressed) stateManager.prev();
    }

    onPressArrow(e) {
        this.bus.dispatchEvent(new CustomEvent('onPressArrow', { detail: { originalEvent: e } }));
    }

    onPressD(e) {
        if (this.isCtrlPressed && !selection.isEmpty) {
            e.preventDefault();
            selection.duplicate();
        }
    }

    onPressCtrl(e) {}
    onStopPressCtrl() {}

    onPressSpace(e) {
        e.preventDefault();
        if (!graphic.containEditionMode(PanEditionMode) && document.activeElement === document.body) {
            this.prevEditionsModes = graphic.editionsModes;
            graphic.setEditionMode(new PanEditionMode());
            this.panAddedFromSpacePress = true;
            graphicEvents.trigger('SET_TOOL', 'pan');
        } else if (graphic.containEditionMode(PanEditionMode) && document.activeElement === document.body) {
            graphic.getEditionMode(PanEditionMode).zoomAtScroll = true;
            document.body.style.overflow = 'hidden';
        }
    }

    onStopPressSpace() {
        if (this.panAddedFromSpacePress) {
            this.panAddedFromSpacePress = false;
            graphic.unsetEditionMode();
            this.resetPrevEditionMode();
        } else if (graphic.containEditionMode(PanEditionMode)) {
            graphic.getEditionMode(PanEditionMode).zoomAtScroll = false;
            document.body.style.overflow = 'auto';
        }
    }

    bindEvents(object) {
        // graphic.log('Bind events on object : ', object);
        if (typeof object.onStartClick !== 'undefined') {
            object.bindStartClick = object.onStartClick.bind(object);
            this.bus.addEventListener('onStartClick', object.bindStartClick);
        }
        if (typeof object.onFinishClick !== 'undefined') {
            object.bindFinishClick = object.onFinishClick.bind(object);
            this.bus.addEventListener('onFinishClick', object.bindFinishClick);
        }
        if (typeof object.onCancelClick !== 'undefined') {
            object.bindCancelClick = object.onCancelClick.bind(object);
            this.bus.addEventListener('onCancelClick', object.bindCancelClick);
        }
        if (typeof object.onMoveMouse !== 'undefined') {
            object.bindMoveMouse = object.onMoveMouse.bind(object);
            this.bus.addEventListener('onMoveMouse', object.bindMoveMouse);
        }
        if (typeof object.onPressDelete !== 'undefined') {
            object.bindPressDelete = object.onPressDelete.bind(object);
            this.bus.addEventListener('onPressDelete', object.bindPressDelete);
        }
        if (typeof object.onPressArrow !== 'undefined') {
            object.bindPressArrow = object.onPressArrow.bind(object);
            this.bus.addEventListener('onPressArrow', object.bindPressArrow);
        }
        if (typeof object.onStopClick !== 'undefined') {
            object.bindStopClick = object.onStopClick.bind(object);
            this.bus.addEventListener('onFinishClick', object.bindStopClick);
            this.bus.addEventListener('onCancelClick', object.bindStopClick);
        }
    }

    unbindEvents(object) {
        if (typeof object.bindStartClick !== 'undefined') {
            this.bus.removeEventListener('onStartClick', object.bindStartClick);
        }
        if (typeof object.bindFinishClick !== 'undefined') {
            this.bus.removeEventListener('onFinishClick', object.bindFinishClick);
        }
        if (typeof object.bindCancelClick !== 'undefined') {
            this.bus.removeEventListener('onCancelClick', object.bindCancelClick);
        }
        if (typeof object.bindMoveMouse !== 'undefined') {
            this.bus.removeEventListener('onMoveMouse', object.bindMoveMouse);
        }
        if (typeof object.bindPressDelete !== 'undefined') {
            this.bus.removeEventListener('onPressDelete', object.bindPressDelete);
        }
        if (typeof object.bindPressArrow !== 'undefined') {
            this.bus.removeEventListener('onPressArrow', object.bindPressArrow);
        }
        if (typeof object.bindStopClick !== 'undefined') {
            this.bus.removeEventListener('onFinishClick', object.bindStopClick);
            this.bus.removeEventListener('onCancelClick', object.bindStopClick);
        }
    }

    resetPrevEditionMode() {
        const objs = Object.values(this.prevEditionsModes).map(editionMode => {
            switch (editionMode.constructor) {
                case SelectionEditionMode: {
                    graphicEvents.trigger('SET_TOOL', 'selection');
                    break;
                }
                case RowEditionMode: {
                    graphicEvents.trigger('SET_TOOL', 'entity');
                    break;
                }
                case TextEditionMode: {
                    graphicEvents.trigger('SET_TOOL', 'text');
                    break;
                }
                case PitEditionMode: {
                    graphicEvents.trigger('SET_TOOL', 'pit');
                    break;
                }
                default:
                    break;
            }
            return new editionMode.constructor()
        });
        graphic.setEditionMode(...objs);
    }
}

export const containerEvents = new ContainerEvents();